/**
 * Pure rules behind the sidebar project switcher (`components/project-switcher.tsx`).
 * Kept free of React/DOM so the ">5 projects" rule and the matcher are unit-testable.
 */

export interface SwitcherProject {
  id: string
  name: string
}

export interface SwitcherGroup<P extends SwitcherProject = SwitcherProject> {
  organization: { id: string; name: string }
  projects: P[]
}

/** The filter box appears once the user can reach more projects than this. */
export const PROJECT_FILTER_THRESHOLD = 5

export function countSwitcherProjects(groups: readonly SwitcherGroup[]): number {
  return groups.reduce((n, g) => n + g.projects.length, 0)
}

/** More projects than `PROJECT_FILTER_THRESHOLD`, counted across every organization. */
export function shouldShowProjectFilter(groups: readonly SwitcherGroup[]): boolean {
  return countSwitcherProjects(groups) > PROJECT_FILTER_THRESHOLD
}

/**
 * Case- and accent-insensitive "contains" on the project name. Whitespace-only queries match
 * everything; organizations left without a match disappear; order is preserved.
 */
export function filterProjectGroups<G extends SwitcherGroup>(
  groups: readonly G[],
  query: string
): G[] {
  const needle = normalize(query)
  return groups
    .map((g) =>
      needle ? { ...g, projects: g.projects.filter((p) => normalize(p.name).includes(needle)) } : g
    )
    .filter((g) => g.projects.length > 0)
}

function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
}

/**
 * Focusing the filter box on open is a win with a physical keyboard and a loss on touch,
 * where it pops the on-screen keyboard over the list the user came to tap.
 */
export function shouldAutofocusProjectFilter(env: {
  phone: boolean
  coarsePointer: boolean
}): boolean {
  return !env.phone && !env.coarsePointer
}
